/* Supply › Reports › Stock › Low Stock Report
 * Batches whose available quantity has fallen below a threshold. */
import React, { useMemo, useState } from "react";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx } from "./reportUtils";
import { ReportBack, RackCell, useWarehouseLots } from "./reportShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

const StockLow = () => {
  /* Store comes from the topbar switcher, app-wide. */
  const { wh, lots, loading, error, reload } = useWarehouseLots();
  const [threshold, setThreshold] = useState(100);

  const rows = useMemo(
    () =>
      lots
        .filter((l) => l.qtyA < threshold)
        .sort((a, b) => a.qtyA - b.qtyA),
    [lots, threshold]
  );

  const totalUnits = rows.reduce((s, l) => s + l.qtyA, 0);

  const handleExport = () => {
    const headers = ["Medicine", "Code", "Batch", "Rack", "Qty received", "Qty available", "% left", "Warehouse"];
    const body = rows.map((l) => [
      l.medicineName || "",
      l.medId || "",
      l.batch || "",
      l.rackCode || "",
      l.qtyR,
      l.qtyA,
      l.qtyR ? Math.round((l.qtyA / l.qtyR) * 100) : 0,
      l.wh || "",
    ]);
    exportToXlsx(`low-stock-${wh || "all"}.xlsx`, headers, body, "Low stock");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.STOCK_REPORT} label="Stock Reports" />
      <SectionHeader
        title="Low Stock Report"
        sub="Batches running low — candidates for reorder"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!rows.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Below</span>
        <Input
          type="number"
          value={threshold}
          onChange={(e) => setThreshold(Math.max(0, Number(e.target.value) || 0))}
          style={{ width: 100, maxWidth: "100%" }}
        />
        <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>units</span>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(rows.length)} batches
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-2" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Low-stock batches" value={cbFmtNum(rows.length)} variant="red" />
        <KpiBox label="Units remaining" value={cbFmtNum(totalUnits)} variant="amber" />
      </div>

      {loading ? (
        <LoadingState label="Loading low stock report…" />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <Table
          columns={[
            { label: "Medicine" },
            { label: "Code" },
            { label: "Batch" },
            { label: "Rack" },
            { label: "Received", align: "right" },
            { label: "Available", align: "right" },
            { label: "% left", align: "right" },
            { label: "Warehouse" },
          ]}
        >
          {rows.length === 0 && (
            <tr>
              <Td colSpan={8} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                No batches below {threshold} units.
              </Td>
            </tr>
          )}
          {rows.map((l) => {
            const pct = l.qtyR ? Math.round((l.qtyA / l.qtyR) * 100) : 0;
            return (
              <Tr key={l.id}>
                <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l.medicineName}</Td>
                <Td mono>{l.medId}</Td>
                <Td mono>{l.batch}</Td>
                <RackCell lot={l} />
                <Td mono right>
                  {cbFmtNum(l.qtyR)}
                </Td>
                <Td mono right style={{ color: pct < 30 ? "var(--red-700)" : "var(--amber-700)", fontWeight: 600 }}>
                  {cbFmtNum(l.qtyA)}
                </Td>
                <Td mono right>
                  {pct}%
                </Td>
                <Td>
                  <Pill variant="neutral">{l.wh}</Pill>
                </Td>
              </Tr>
            );
          })}
        </Table>
      )}
    </div>
  );
};

export default StockLow;
